import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

export type GradientCenteredVariant = 'blue' | 'green' | 'red' | 'purple' | 'amber' | 'default' | 'destructive';

// Remove all animation and drag-related event props to avoid conflicts between React and Framer Motion
type MotionButtonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>, 
  | 'onDrag' | 'onDragEnd' | 'onDragStart' | 'onDragEnter' | 'onDragLeave' | 'onDragOver' | 'onDragExit'
  | 'onAnimationStart' | 'onAnimationEnd' | 'onAnimationIteration'
>;

export interface GradientButtonCenteredProps extends MotionButtonProps {
  variant?: GradientCenteredVariant;
  icon?: React.ReactNode;
  isLoading?: boolean;
  fullWidth?: boolean;
  testId?: string;
}

/**
 * GradientButtonCentered component with beautiful glassy gradient styling
 * Used for primary actions with premium visual effects
 */
const GradientButtonCentered = React.forwardRef<HTMLButtonElement, GradientButtonCenteredProps>(
  ({ 
    children, 
    variant = 'blue', 
    icon, 
    isLoading = false,
    fullWidth = true,
    className,
    disabled,
    testId,
    ...props 
  }, ref) => {
    // Enhanced gradient variants with glassy effects
    const gradients = {
      blue: 'bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700 hover:from-blue-400 hover:via-blue-500 hover:to-indigo-600',
      green: 'bg-gradient-to-br from-green-500 via-green-600 to-emerald-700 hover:from-green-400 hover:via-green-500 hover:to-emerald-600',
      red: 'bg-gradient-to-br from-red-500 via-red-600 to-rose-700 hover:from-red-400 hover:via-red-500 hover:to-rose-600',
      purple: 'bg-gradient-to-br from-purple-600 via-purple-700 to-violet-800 hover:from-purple-500 hover:via-purple-600 hover:to-violet-700',
      amber: 'bg-gradient-to-br from-amber-500 via-orange-600 to-yellow-700 hover:from-amber-400 hover:via-orange-500 hover:to-yellow-600',
      default: 'bg-gradient-to-br from-gray-500 via-gray-600 to-slate-700 hover:from-gray-400 hover:via-gray-500 hover:to-slate-600',
      destructive: 'bg-gradient-to-br from-red-500 via-red-600 to-rose-700 hover:from-red-400 hover:via-red-500 hover:to-rose-600',
    };

    // Ring colors for each variant
    const ringColors = {
      blue: 'ring-blue-200/50 dark:ring-blue-800/50 hover:ring-blue-300/70 dark:hover:ring-blue-700/70',
      green: 'ring-green-200/50 dark:ring-green-800/50 hover:ring-green-300/70 dark:hover:ring-green-700/70',
      red: 'ring-red-200/50 dark:ring-red-800/50 hover:ring-red-300/70 dark:hover:ring-red-700/70',
      purple: 'ring-purple-200/50 dark:ring-purple-800/50 hover:ring-purple-300/70 dark:hover:ring-purple-700/70',
      amber: 'ring-amber-200/50 dark:ring-amber-800/50 hover:ring-amber-300/70 dark:hover:ring-amber-700/70',
      default: 'ring-gray-200/50 dark:ring-gray-800/50 hover:ring-gray-300/70 dark:hover:ring-gray-700/70',
      destructive: 'ring-red-200/50 dark:ring-red-800/50 hover:ring-red-300/70 dark:hover:ring-red-700/70',
    };

    // Border colors for each variant
    const borderColors = {
      blue: 'border-blue-300/30 hover:border-blue-400/50',
      green: 'border-green-300/30 hover:border-green-400/50',
      red: 'border-red-300/30 hover:border-red-400/50',
      purple: 'border-purple-300/30 hover:border-purple-400/50',
      amber: 'border-amber-300/30 hover:border-amber-400/50',
      default: 'border-gray-300/30 hover:border-gray-400/50',
      destructive: 'border-red-300/30 hover:border-red-400/50',
    };

    // Shadow colors for each variant
    const shadowColors = {
      blue: 'shadow-2xl hover:shadow-3xl hover:shadow-blue-500/40',
      green: 'shadow-2xl hover:shadow-3xl hover:shadow-green-500/40',
      red: 'shadow-2xl hover:shadow-3xl hover:shadow-red-500/40',
      purple: 'shadow-2xl hover:shadow-3xl hover:shadow-purple-500/40',
      amber: 'shadow-2xl hover:shadow-3xl hover:shadow-amber-500/40',
      default: 'shadow-2xl hover:shadow-3xl hover:shadow-gray-500/40',
      destructive: 'shadow-2xl hover:shadow-3xl hover:shadow-red-500/40',
    };

    // Animation props for the motion button
    const motionProps = {
      whileHover: (!disabled && !isLoading) ? { scale: 1.03 } : undefined,
      whileTap: (!disabled && !isLoading) ? { scale: 0.97 } : undefined,
    };

    return (
      <motion.button
        ref={ref}
        {...motionProps}
        disabled={disabled || isLoading}
        data-test-id={testId}
        className={cn(
          // Base styles with enhanced aesthetics
          'relative group h-12 py-3 text-sm font-medium rounded-xl',
          'transition-all duration-500 ease-out',
          'flex items-center justify-center gap-2',
          
          // Beautiful glassy gradient styling
          gradients[variant],
          
          // Enhanced shadows and depth
          shadowColors[variant],
          
          // Ring effects for premium feel
          'ring-4',
          ringColors[variant],
          
          // Borders for definition
          'border',
          borderColors[variant],
          
          // Glassy overlay effects
          'before:absolute before:inset-0 before:rounded-xl before:bg-gradient-to-br before:from-white/20 before:to-transparent',
          'before:opacity-0 before:transition-all before:duration-500 hover:before:opacity-100',
          'after:absolute after:inset-0 after:rounded-xl after:bg-gradient-to-br after:from-transparent after:to-black/10',
          
          // Text styling
          'text-white font-semibold',
          
          // Width
          fullWidth ? 'w-full' : 'w-auto',
          
          // Disabled state
          (disabled || isLoading) && 'opacity-70 cursor-not-allowed hover:scale-100 hover:before:opacity-0',
          
          // Custom classes
          className
        )}
        {...props}
      >
        {/* Content with proper z-index */}
        <div className="relative z-10 flex items-center justify-center gap-2">
          {isLoading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin drop-shadow-sm" />
              <span className="mx-1 drop-shadow-sm">{typeof children === 'string' ? 'Loading...' : children}</span>
            </>
          ) : (
            <>
              {icon && <span className="flex-shrink-0 drop-shadow-sm">{icon}</span>}
              <span className="mx-1 drop-shadow-sm">{children}</span>
            </>
          )}
        </div>
        
        {/* Clean magical aura effect */}
        <div className={cn(
          "absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 blur-md scale-125 group-hover:scale-150",
          "transition-all duration-700 ease-out pointer-events-none",
          variant === 'purple' && "bg-gradient-to-br from-purple-500/30 via-purple-600/20 to-violet-700/30",
          variant === 'blue' && "bg-gradient-to-br from-blue-500/30 via-indigo-500/20 to-blue-600/30",
          variant === 'green' && "bg-gradient-to-br from-green-500/30 via-emerald-500/20 to-green-600/30",
          variant === 'red' && "bg-gradient-to-br from-red-500/30 via-rose-500/20 to-red-600/30",
          variant === 'amber' && "bg-gradient-to-br from-amber-500/30 via-orange-500/20 to-yellow-600/30",
          variant === 'default' && "bg-gradient-to-br from-gray-500/30 via-slate-500/20 to-gray-600/30",
          variant === 'destructive' && "bg-gradient-to-br from-red-500/30 via-rose-500/20 to-red-600/30"
        )}></div>
      </motion.button>
    );
  }
);

GradientButtonCentered.displayName = 'GradientButtonCentered';

export default GradientButtonCentered;
